import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { getEligibleTypes, type LeaveBalances, type LeaveTypeConfig } from '@/api/leaves'

// Fallback names for the built-in types, used only until the org's configured
// leave types have loaded (or if that request fails).
const LEAVE_TYPE_LABELS: Record<string, string> = {
  casual: 'Casual Leave', sick: 'Sick Leave', urgent: 'Urgent Leave', annual: 'Annual Leave', wfh: 'Work From Home',
}

/** "paternity_leave" → "Paternity Leave" — last-resort label for an unknown key. */
function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Total / Availed / Pending / Available per leave type. Names come from the org's
 *  configured leave types (same source as the Leaves page), in the same order. */
export default function LeaveSummaryCard({ balances }: { balances?: LeaveBalances }) {
  const { user } = useAuthStore()
  const { data: types = [] } = useQuery({
    queryKey: ['leave-types', 'eligible', user?.id],
    queryFn: getEligibleTypes,
    enabled: !!user?.id,
  })

  if (!balances) return <div className="text-sm text-gray-400">Loading…</div>
  const entries = Object.entries(balances)
  if (entries.length === 0) return <div className="text-sm text-gray-400">No leave types configured.</div>

  const labelOf = (key: string) =>
    types.find((t: LeaveTypeConfig) => t.key === key)?.label || LEAVE_TYPE_LABELS[key] || humanize(key)
  // Configured order first (as on the Leaves page), then anything else.
  const order = (key: string) => {
    const i = types.findIndex((t: LeaveTypeConfig) => t.key === key)
    return i === -1 ? Number.MAX_SAFE_INTEGER : i
  }
  entries.sort(([a], [b]) => order(a) - order(b))

  return (
    <div className="space-y-4">
      {entries.map(([type, b]) => {
        const total = b.total || 0
        const usedPct = total ? Math.min(100, Math.round((b.used / total) * 100)) : 0
        const pendingPct = total ? Math.min(100 - usedPct, Math.round((b.pending / total) * 100)) : 0
        return (
          <div key={type}>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-sm font-medium text-gray-700">{labelOf(type)}</span>
              {b.period === 'month' && <span className="text-[11px] text-gray-400">this month</span>}
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden flex">
              <div className="h-full bg-brand" style={{ width: `${usedPct}%` }} />
              <div className="h-full bg-amber-300" style={{ width: `${pendingPct}%` }} />
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-gray-400">
              <span>Total: <span className="text-gray-600 font-medium">{total}</span></span>
              <span>Availed: <span className="text-gray-600 font-medium">{b.used}</span></span>
              <span>Pending: <span className="text-gray-600 font-medium">{b.pending}</span></span>
              <span>Available: <span className="text-gray-600 font-medium">{b.remaining}</span></span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
